/**
 * Status dos estudos do Flux Personalizado na tela nova "Meus Estudos" (#1982).
 *
 * As rotas novas (`produtos/previvaz-personalizado/estudos/*`) mandam o status
 * por CÓDIGO estável, não pelo texto de `dim_flux_personalizado_status`. Assim
 * o front não depende mais de comparar o rótulo (`helper/studies.ts`), que hoje
 * já esquece o "Executando" no `StatusTag`.
 *
 * Cores: paleta do Claude Design (decisão D25, `.dc.html:567`), pílula clara com
 * bolinha. É exceção declarada à regra de usar só tokens de `custom-colors.scss`.
 * O `StatusTag` do Flux Upload e do get-list legado NÃO usa esta tabela.
 */

export type FluxStudyStatusCode = 'em_fila' | 'executando' | 'pronto' | 'erro' | 'cancelado';

export interface FluxStudyStatusMeta {
  /** Código do contrato, ou `null` no fallback de código desconhecido. */
  code: FluxStudyStatusCode | null;
  /** Rótulo exibido na UI (pt-BR). */
  label: string;
  /** Fundo da pílula. */
  background: string;
  /** Cor do texto da pílula. */
  text: string;
  /** Cor da bolinha à esquerda do rótulo. */
  dot: string;
}

/** Ordem de exibição (filtro de status e legendas). */
export const FLUX_STUDY_STATUS_CODES: readonly FluxStudyStatusCode[] = [
  'em_fila',
  'executando',
  'pronto',
  'erro',
  'cancelado',
];

/** Status ainda não finais, acompanhados pelo polling de status (nota 4, `.dc.html:50`). */
export const ACTIVE_FLUX_STUDY_STATUSES: readonly FluxStudyStatusCode[] = ['em_fila', 'executando'];

/**
 * O estudo ainda pode mudar de status? Em Fila, Executando ou código desconhecido (`null`): um
 * código novo do backend não é final, como no `isFinished` da linha (`helper/flux/myStudyRow`), e
 * segue no polling até virar um dos finais.
 *
 * @param code código do adapter (`null` quando ele não reconheceu o código)
 * @returns `true` para Em Fila, Executando e desconhecido
 */
export function isActiveFluxStudyStatus(code: FluxStudyStatusCode | null): boolean {
  return code === null || ACTIVE_FLUX_STUDY_STATUSES.includes(code);
}

export const FLUX_STUDY_STATUS: Readonly<Record<FluxStudyStatusCode, FluxStudyStatusMeta>> = {
  em_fila: {
    code: 'em_fila', label: 'Em Fila', background: 'var(--ee-bg-muted)', text: 'var(--ee-text-secondary)', dot: 'var(--ee-neutral-solid)',
  },
  executando: {
    code: 'executando', label: 'Executando', background: 'var(--ee-info-bg)', text: 'var(--ee-info-fg)', dot: 'var(--ee-info-solid)',
  },
  pronto: {
    code: 'pronto', label: 'Estudo Pronto', background: 'var(--ee-success-bg)', text: 'var(--ee-success-fg)', dot: 'var(--ee-success-solid)',
  },
  erro: {
    code: 'erro', label: 'Com Erro', background: 'var(--ee-danger-bg)', text: 'var(--ee-danger-fg)', dot: 'var(--ee-danger-solid)',
  },
  cancelado: {
    code: 'cancelado', label: 'Cancelado', background: 'var(--ee-bg-muted)', text: 'var(--ee-text-muted)', dot: 'var(--ee-neutral-solid)',
  },
};

/**
 * Fallback para um código que o front ainda não conhece (ex.: o backend criou
 * um status novo). Neutro: fundo `bg-subtle`, texto `text-secondary`, bolinha `neutral-solid`.
 */
export const UNKNOWN_FLUX_STUDY_STATUS: FluxStudyStatusMeta = {
  code: null,
  label: 'Status desconhecido',
  background: 'var(--ee-bg-subtle)',
  text: 'var(--ee-text-secondary)',
  dot: 'var(--ee-neutral-solid)',
};

/** Type guard: o valor recebido do wire é um dos 5 códigos do contrato? */
export function isFluxStudyStatusCode(value: string): value is FluxStudyStatusCode {
  return (FLUX_STUDY_STATUS_CODES as readonly string[]).includes(value);
}

/**
 * Metadados de exibição de um status.
 *
 * @param code código recebido (ou `null`/`undefined` quando o adapter não reconheceu)
 * @param fallbackLabel rótulo do backend (`status_rotulo`), usado só no fallback
 * @returns a entrada da tabela, ou o fallback neutro para código desconhecido
 */
export function getFluxStudyStatusMeta(
  code: string | null | undefined,
  fallbackLabel?: string,
): FluxStudyStatusMeta {
  if (code && isFluxStudyStatusCode(code)) {
    return FLUX_STUDY_STATUS[code];
  }

  const label = fallbackLabel?.trim();
  return label ? { ...UNKNOWN_FLUX_STUDY_STATUS, label } : UNKNOWN_FLUX_STUDY_STATUS;
}
